import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Mail, Phone, Building2, MessageSquare, Calendar, User, PhoneCall } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [submissions, setSubmissions] = useState([]);
    const [callbacks, setCallbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [expandedRow, setExpandedRow] = useState(null);
    const [activeTab, setActiveTab] = useState("messages");

    const getToken = () => sessionStorage.getItem("adminToken");

    const fetchSubmissions = useCallback(async () => {
        setLoading(true);
        setError("");
        const token = getToken();
        if (!token) {
            navigate("/admin", { replace: true });
            return;
        }
        try {
            const [subRes, cbRes] = await Promise.all([
                fetch(`${BACKEND_URL}/api/contact/submissions`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                fetch(`${BACKEND_URL}/api/contact/callbacks`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
            ]);
            if (subRes.status === 401 || cbRes.status === 401) {
                sessionStorage.removeItem("adminToken");
                sessionStorage.removeItem("adminEmail");
                navigate("/admin", { replace: true });
                return;
            }
            const subData = await subRes.json();
            const cbData = await cbRes.json();
            if (!subRes.ok) throw new Error(subData.error || "Failed to fetch submissions");
            if (!cbRes.ok) throw new Error(cbData.error || "Failed to fetch callbacks");
            setSubmissions(subData.submissions || []);
            setCallbacks(cbData.callbacks || []);
        } catch (err) {
            setError(err.message || "Failed to load data");
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        const token = getToken();
        if (!token) {
            navigate("/admin", { replace: true });
            return;
        }
        fetchSubmissions();
    }, [navigate, fetchSubmissions]);

    const handleLogout = () => {
        sessionStorage.removeItem("adminToken");
        sessionStorage.removeItem("adminEmail");
        navigate("/admin", { replace: true });
    };

    const formatDate = (isoString) => {
        if (!isoString) return "—";
        const d = new Date(isoString);
        return d.toLocaleString("en-IN", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900">Admin Dashboard</h1>
                        <p className="text-sm text-gray-500">Contact Form Submissions</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-500 hidden sm:inline">
                            {sessionStorage.getItem("adminEmail")}
                        </span>
                        <button
                            onClick={fetchSubmissions}
                            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
                            title="Refresh"
                        >
                            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                        </button>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                        >
                            <LogOut size={16} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
                        <div className="bg-black rounded-lg p-3">
                            <MessageSquare className="text-white" size={20} />
                        </div>
                        <div>
                            <p className="text-2xl font-semibold text-gray-900">{submissions.length}</p>
                            <p className="text-sm text-gray-500">Message Submissions</p>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
                        <div className="bg-black rounded-lg p-3">
                            <PhoneCall className="text-white" size={20} />
                        </div>
                        <div>
                            <p className="text-2xl font-semibold text-gray-900">{callbacks.length}</p>
                            <p className="text-sm text-gray-500">Callback Requests</p>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
                    <button
                        onClick={() => setActiveTab("messages")}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "messages" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                    >
                        Messages ({submissions.length})
                    </button>
                    <button
                        onClick={() => setActiveTab("callbacks")}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "callbacks" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                    >
                        Callbacks ({callbacks.length})
                    </button>
                </div>

                {error && (
                    <div className="bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-3 text-sm mb-6">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-20">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-black mx-auto mb-4"></div>
                        <p className="text-gray-500">Loading submissions...</p>
                    </div>
                ) : activeTab === "messages" ? (
                    submissions.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
                            <MessageSquare className="mx-auto text-gray-300 mb-4" size={48} />
                            <p className="text-gray-500 text-lg">No submissions yet</p>
                            <p className="text-gray-400 text-sm mt-1">Contact form submissions will appear here</p>
                        </div>
                    ) : (
                        <div>
                            <div className="hidden md:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-gray-50 border-b border-gray-200">
                                                <th className="text-left px-5 py-3 font-medium text-gray-600">#</th>
                                                <th className="text-left px-5 py-3 font-medium text-gray-600">Full Name</th>
                                                <th className="text-left px-5 py-3 font-medium text-gray-600">Email Address</th>
                                                <th className="text-left px-5 py-3 font-medium text-gray-600">Phone Number</th>
                                                <th className="text-left px-5 py-3 font-medium text-gray-600">Company Name</th>
                                                <th className="text-left px-5 py-3 font-medium text-gray-600">Message</th>
                                                <th className="text-left px-5 py-3 font-medium text-gray-600">Date & Time</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {submissions.map((sub, idx) => (
                                                <tr
                                                    key={sub.id}
                                                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                                                    onClick={() => setExpandedRow(expandedRow === sub.id ? null : sub.id)}
                                                >
                                                    <td className="px-5 py-3 text-gray-400">{idx + 1}</td>
                                                    <td className="px-5 py-3 font-medium text-gray-900">{sub.fullName}</td>
                                                    <td className="px-5 py-3 text-gray-700">{sub.emailAddress}</td>
                                                    <td className="px-5 py-3 text-gray-700">{sub.phoneNumber || "—"}</td>
                                                    <td className="px-5 py-3 text-gray-700">{sub.companyName || "—"}</td>
                                                    <td className="px-5 py-3 text-gray-700 max-w-xs">
                                                        {expandedRow === sub.id ? (
                                                            <span className="whitespace-pre-wrap">{sub.message || "—"}</span>
                                                        ) : (
                                                            <span className="truncate block max-w-xs">
                                                                {sub.message ? (sub.message.length > 60 ? sub.message.slice(0, 60) + "..." : sub.message) : "—"}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{formatDate(sub.createdAt)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="md:hidden space-y-4 mt-4">
                                {submissions.map((sub, idx) => (
                                    <div key={sub.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-400">#{idx + 1}</span>
                                            <span className="text-xs text-gray-500 flex items-center gap-1">
                                                <Calendar size={12} /> {formatDate(sub.createdAt)}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-900 font-medium">
                                            <User size={16} className="text-gray-400" /> {sub.fullName}
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-700 text-sm">
                                            <Mail size={14} className="text-gray-400" /> {sub.emailAddress}
                                        </div>
                                        {sub.phoneNumber && (
                                            <div className="flex items-center gap-2 text-gray-700 text-sm">
                                                <Phone size={14} className="text-gray-400" /> {sub.phoneNumber}
                                            </div>
                                        )}
                                        {sub.companyName && (
                                            <div className="flex items-center gap-2 text-gray-700 text-sm">
                                                <Building2 size={14} className="text-gray-400" /> {sub.companyName}
                                            </div>
                                        )}
                                        {sub.message && (
                                            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                                                {sub.message}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )
                ) : (
                    callbacks.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
                            <PhoneCall className="mx-auto text-gray-300 mb-4" size={48} />
                            <p className="text-gray-500 text-lg">No callback requests yet</p>
                            <p className="text-gray-400 text-sm mt-1">Callback requests will appear here</p>
                        </div>
                    ) : (
                        <div>
                            <div className="hidden md:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="bg-gray-50 border-b border-gray-200">
                                                <th className="text-left px-5 py-3 font-medium text-gray-600">#</th>
                                                <th className="text-left px-5 py-3 font-medium text-gray-600">Phone Number</th>
                                                <th className="text-left px-5 py-3 font-medium text-gray-600">Request Type</th>
                                                <th className="text-left px-5 py-3 font-medium text-gray-600">Date & Time</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {callbacks.map((cb, idx) => (
                                                <tr key={cb.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                                                    <td className="px-5 py-3 text-gray-400">{idx + 1}</td>
                                                    <td className="px-5 py-3 font-medium text-gray-900">{cb.phoneNumber}</td>
                                                    <td className="px-5 py-3">
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                                                            <PhoneCall size={12} /> Callback
                                                        </span>
                                                    </td>
                                                    <td className="px-5 py-3 text-gray-500 whitespace-nowrap">{formatDate(cb.createdAt)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="md:hidden space-y-4 mt-4">
                                {callbacks.map((cb, idx) => (
                                    <div key={cb.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-400">#{idx + 1}</span>
                                            <span className="text-xs text-gray-500 flex items-center gap-1">
                                                <Calendar size={12} /> {formatDate(cb.createdAt)}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-900 font-medium">
                                            <Phone size={16} className="text-gray-400" /> {cb.phoneNumber}
                                        </div>
                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                                            <PhoneCall size={12} /> Callback Request
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )
                )}
            </main>
        </div>
    );
}
